// Selected-day route list for the Logistics calendar mode.
import { SectionCard } from "@/components/common/SectionCard";
import {
  ROUTE_STATE_CHIP_CLASSES,
  ROUTE_STATE_LABELS,
  carrierLabel,
  formatRouteScheduled,
  ghostBtnClass,
  unitLabel,
} from "./logisticsRoutesShared";
import type { LogisticsCalendarDayRollup } from "@/lib/logistics/calendarLocalWeek";
import type { LogiCarrier } from "@/lib/logistics";
import type { MfgUnit } from "@/lib/manufacturing/types";

type Props = {
  rollup: LogisticsCalendarDayRollup | null;
  units: MfgUnit[];
  carriers: LogiCarrier[];
  selectedRouteId: string | null;
  onSelectRoute: (routeId: string) => void;
};

export function LogisticsCalendarDayRoutes({
  rollup,
  units,
  carriers,
  selectedRouteId,
  onSelectRoute,
}: Props) {
  if (!rollup) {
    return (
      <SectionCard title="Day detail">
        <p className="text-sm text-gray-500 dark:text-zinc-400">
          Select a day on the week grid to see scheduled deliveries.
        </p>
      </SectionCard>
    );
  }

  const heading = new Date(rollup.date + "T12:00:00").toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <SectionCard title={`${heading} — deliveries`}>
      {rollup.routes.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">No routes scheduled this day.</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-zinc-700">
          {rollup.routes.map((route) => {
            const chipClass =
              ROUTE_STATE_CHIP_CLASSES[route.route_state] ?? "bg-gray-500 text-white";
            const scheduled = formatRouteScheduled(route);
            const isSelected = route.id === selectedRouteId;
            return (
              <li
                key={route.id}
                className={`flex flex-wrap items-start gap-2 py-3 ${
                  isSelected ? "bg-indigo-50 dark:bg-indigo-950/30 -mx-2 px-2 rounded" : ""
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-900 dark:text-zinc-100">
                    <code>{unitLabel(units, route.unit_id)}</code>
                    <span
                      className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${chipClass}`}
                    >
                      {ROUTE_STATE_LABELS[route.route_state] ?? route.route_state}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-zinc-400">
                    Carrier: {carrierLabel(carriers, route.carrier_id)}
                  </div>
                  {scheduled ? (
                    <div className="text-xs text-gray-600 dark:text-zinc-400">Window: {scheduled}</div>
                  ) : null}
                </div>
                <button
                  type="button"
                  className={ghostBtnClass}
                  onClick={() => onSelectRoute(route.id)}
                  aria-pressed={isSelected}
                >
                  {isSelected ? "Close" : "Open"}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </SectionCard>
  );
}
